import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CloudUpload, 
  Storage, 
  Add,
  Folder,
  FolderOpen,
  Dataset,
  Refresh,
  Delete,
  Search
} from '@mui/icons-material';
import { TextField, InputAdornment } from '@mui/material';
import DatasetExplorer from '../components/DatasetPreview';
import DatasetUploadModal from '../components/DatasetUploadModal';
import { API_URL, fetchWithCredentials } from '../config';

const DatasetsPage = () => {
  const [corpuses, setCorpuses] = useState([]);
  const [selectedCorpus, setSelectedCorpus] = useState(null);
  const [selectedDatasetTab, setSelectedDatasetTab] = useState(0);
  const [datasets, setDatasets] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewingDataset, setViewingDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Load available corpuses on mount
  useEffect(() => {
    loadCorpuses();
  }, []);

  const loadCorpuses = async () => {
    try {
      let currentCorpusId = localStorage.getItem('corpus_id');
      
      // Build corpus list - demo corpus first
      const corpusList = [];
      
      // Always add demo corpus
      corpusList.push({
        id: 'demo-skipper-corpus-2024',
        name: 'Skipper Demo',
        type: 'demo',
        description: '18 pre-loaded datasets for exploration'
      });
      
      // Add personal corpus if exists and is different from demo
      if (currentCorpusId && currentCorpusId !== 'demo-skipper-corpus-2024') {
        corpusList.push({
          id: currentCorpusId,
          name: 'My Data',
          type: 'personal',
          description: 'Your uploaded datasets'
        });
      }
      
      setCorpuses(corpusList);
      
      // Set default to Skipper Demo if no corpus selected
      if (!currentCorpusId) {
        const demoCorpus = corpusList.find(c => c.type === 'demo');
        if (demoCorpus) {
          localStorage.setItem('corpus_id', demoCorpus.id);
          console.log('✅ Set default corpus: Skipper Demo');
          setSelectedCorpus(demoCorpus);
          // Load datasets for demo corpus
          loadDatasetsForCorpus(demoCorpus.id);
        }
      } else {
        // Auto-select current corpus
        const current = corpusList.find(c => c.id === currentCorpusId);
        if (current) {
          setSelectedCorpus(current);
          loadDatasetsForCorpus(current.id);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading corpuses:', error);
      setLoading(false);
    }
  };

  const handleSelectCorpus = async (corpus) => {
    setSelectedCorpus(corpus);
    
    // Load datasets for this corpus
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/datasets`);
      const data = await response.json();
      
      if (data.datasets) {
        setDatasets(data.datasets);
        console.log(`Loaded ${data.datasets.length} datasets from corpus ${corpus.id}`);
      }
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };
  
  const handleSetActiveCorpus = () => {
    if (!selectedCorpus) return;
    
    // Save to localStorage
    localStorage.setItem('corpus_id', selectedCorpus.id);
    console.log(`✅ Set active corpus: ${selectedCorpus.name} (${selectedCorpus.id})`);
    
    // Reload page to update corpus indicator everywhere
    window.location.reload();
  };

  const loadDatasetsForCorpus = async (corpusId) => {
    try {
      const response = await fetchWithCredentials(`${API_URL}/api/datasets`);
      const data = await response.json();
      
      if (data.datasets) {
        setDatasets(data.datasets);
        console.log(`Loaded ${data.datasets.length} datasets from corpus`);
      }
    } catch (error) {
      console.error('Error loading datasets:', error);
    }
  };

  const handleUpload = (result) => {
    console.log('Upload result:', result);
    setUploadModalOpen(false);
    // Reload datasets
    if (selectedCorpus) {
      loadDatasetsForCorpus(selectedCorpus.id);
    }
  };

  // If no corpus selected, show corpus selector
  if (!selectedCorpus) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Select Data Corpus
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          A corpus is a collection of related datasets. Choose one to explore:
        </Typography>

        {corpuses.length === 0 && !loading && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              No corpus found. Upload datasets to create your first corpus, or try the demo data.
            </Typography>
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
          {/* Create New Corpus Card */}
          <Card 
            sx={{ 
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'primary.main',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              minHeight: 200,
              backgroundColor: 'rgba(71, 189, 198, 0.05)',
              '&:hover': { 
                boxShadow: 6, 
                transform: 'translateY(-2px)',
                backgroundColor: 'rgba(71, 189, 198, 0.1)'
              },
              transition: 'all 0.2s'
            }}
            onClick={() => {
              // Create a new corpus with a default name
              const newCorpusId = `corpus-${Date.now()}`;
              const newCorpus = {
                id: newCorpusId,
                name: `My Corpus ${new Date().toLocaleDateString()}`,
                type: 'personal',
                description: 'New dataset collection'
              };
              setCorpuses([...corpuses, newCorpus]);
              localStorage.setItem('corpus_id', newCorpusId);
              handleSelectCorpus(newCorpus);
            }}
          >
            <Add sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center' }}>
              Create New Corpus
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 1 }}>
              Start with a fresh dataset collection
            </Typography>
          </Card>

          {corpuses.map((corpus) => {
            const isActive = corpus.id === localStorage.getItem('corpus_id');
            
            return (
              <Card 
                key={corpus.id}
                sx={{ 
                  cursor: 'pointer',
                  border: isActive ? 2 : 1,
                  borderColor: isActive ? 'primary.main' : 'divider',
                  '&:hover': { 
                    boxShadow: 6, 
                    transform: 'translateY(-2px)',
                    borderColor: 'primary.main'
                  },
                  transition: 'all 0.2s'
                }}
                onClick={() => handleSelectCorpus(corpus)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    {corpus.type === 'demo' ? <FolderOpen color="primary" fontSize="large" /> : <Folder color="action" fontSize="large" />}
                    <Typography variant="h5" fontWeight={600}>
                      {corpus.name}
                    </Typography>
                    {isActive && (
                      <Chip label="Active" size="small" color="primary" />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {corpus.description}
                  </Typography>
                  {corpus.type === 'demo' && (
                    <Chip label="18 Datasets" size="small" color="primary" variant="outlined" />
                  )}
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    fullWidth
                    variant="outlined"
                  >
                    Open Corpus →
                  </Button>
                </CardActions>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Filter datasets by search query
  const filteredDatasets = datasets.filter(ds => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ds.name?.toLowerCase().includes(query) ||
      ds.title?.toLowerCase().includes(query) ||
      ds.category?.toLowerCase().includes(query) ||
      ds.type?.toLowerCase().includes(query) ||
      ds.description?.toLowerCase().includes(query)
    );
  });
  
  // Sort datasets by order number (from filename like "0. Product Info.csv")
  const sortedDatasets = [...filteredDatasets].sort((a, b) => {
    const orderA = a.order !== undefined ? a.order : 999;
    const orderB = b.order !== undefined ? b.order : 999;
    return orderA - orderB;
  });
  
  // Dataset tabs (dynamic based on what's uploaded)
  const datasetTabs = sortedDatasets.map(ds => ({
    label: ds.name || ds.title || ds.category,
    category: ds.category,
    dataset: ds
  }));
  
  // Group datasets by type for better organization
  const datasetsByType = sortedDatasets.reduce((acc, ds) => {
    const type = ds.type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(ds);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 4 }}>
      {/* Header with corpus info */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => setSelectedCorpus(null)} size="small">
              <Folder />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              {selectedCorpus.name}
            </Typography>
            {selectedCorpus.type === 'demo' && (
              <Chip label="Demo" size="small" color="primary" />
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            {datasets.length} dataset{datasets.length !== 1 ? 's' : ''} in this corpus
          </Typography>
        </Box>
        
        <Box display="flex" gap={2}>
          {selectedCorpus.id !== localStorage.getItem('corpus_id') && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSetActiveCorpus}
            >
              Set as Active Corpus
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Folder />}
            onClick={() => setSelectedCorpus(null)}
          >
            Switch Corpus
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => handleSelectCorpus(selectedCorpus)}
          >
            Refresh
          </Button>
          {selectedCorpus.type !== 'demo' && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setUploadModalOpen(true)}
            >
              Add Dataset
            </Button>
          )}
        </Box>
      </Box>

      {/* Search Bar */}
      {datasets.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search datasets by name, type, or description..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSelectedDatasetTab(0); // Reset to first tab when searching
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              )
            }}
            sx={{ backgroundColor: 'white' }}
          />
        </Box>
      )}

      {/* Dataset Tabs */}
      {sortedDatasets.length > 0 ? (
        <Paper sx={{ borderRadius: 2 }}>
          <Tabs
            value={selectedDatasetTab}
            onChange={(e, newValue) => setSelectedDatasetTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            {datasetTabs.map((tab, index) => (
              <Tab 
                key={index}
                label={
                  <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5} py={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Dataset fontSize="small" />
                      <Typography variant="body2" fontWeight={600}>
                        {tab.dataset.title || tab.label}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={0.5}>
                      {tab.dataset.type && (
                        <Chip 
                          label={tab.dataset.type} 
                          size="small" 
                          sx={{ height: 18, fontSize: '0.7rem' }}
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      <Chip 
                        label={`${tab.dataset.row_count?.toLocaleString()} rows`} 
                        size="small" 
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </Box>
                }
                sx={{ 
                  textTransform: 'none',
                  alignItems: 'flex-start',
                  minHeight: 72
                }}
              />
            ))}
          </Tabs>

          {/* Dataset Content - Show full preview with DatasetExplorer */}
          <Box>
            {datasetTabs[selectedDatasetTab] && (
              <DatasetExplorer
                dataset={datasetTabs[selectedDatasetTab].dataset}
                onClose={null}
                embedded={true}
              />
            )}
          </Box>
        </Paper>
      ) : searchQuery ? (
        <Alert severity="info">
          <Typography variant="body2">
            No datasets match "{searchQuery}". Try a different search term.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="info">
          <Typography variant="body2">
            No datasets in this corpus yet. {selectedCorpus.type !== 'demo' && 'Click "Add Dataset" to upload your first file.'}
          </Typography>
        </Alert>
      )}

      {/* Upload Modal */}
      <DatasetUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        onUpload={handleUpload}
      />

      {/* Dataset Viewer Modal */}
      {viewingDataset && (
        <DatasetExplorer
          dataset={viewingDataset}
          onClose={() => setViewingDataset(null)}
        />
      )}
    </Box>
  );
};

export default DatasetsPage;

